import {
  FieldSize, Size, attributeCriterionField, attributeLabelType, attributeTypeIdOf, comparatorLabelKey, comparatorTakesValue, comparatorsForAttribute, comparatorsForType, criteriaProblems,
  criteriaReducer, measuredCriterionParts, measuredCriterionValue, refieldAttributeRow, refieldRow, statusMembersOf, t,
  type CatalogAttributeKind, type ColumnDef, type CriteriaRow, type ViewComparator,
} from '@bms2/shared';
import { DatePicker, NumericInput, Select, TextInput } from '../controls';
import { CriteriaRows, type CriteriaRowProblem } from './CriteriaRows';
import styles from './ViewCriteriaRows.module.css';

/** What a condition row reads of each column the list offers. */
export type CriteriaColumn = Pick<ColumnDef, 'key' | 'label' | 'type' | 'statusEntity' | 'unit' | 'offByDefault'>;

/**
 * 🔒 A catalog attribute type a condition row offers on the Items list (decisions 0285, 8800): its kind,
 * a Measured one's family units with the default first chosen, an Enumerated one's values. An inactive
 * type is held so a row already naming it still reads its name, and it is never offered to a new row.
 */
export interface CriteriaAttribute {
  id: string;
  name: string;
  kind: CatalogAttributeKind;
  isActive: boolean;
  defaultUnitId: string | null;
  units: readonly { id: string; label: string }[];
  values: readonly { id: string; label: string; isActive: boolean }[];
}

export interface ViewCriteriaRowsProps {
  rows: readonly CriteriaRow[];
  columns: readonly CriteriaColumn[];
  /** The Items list's attribute types, offered after the columns under the Attributes heading (decision 8800); absent elsewhere. */
  attributes?: readonly CriteriaAttribute[];
  /** True once Save or Apply was pressed, so a refused row reads its reason (decision 2172). */
  checked: boolean;
  onRows: (next: (rows: readonly CriteriaRow[]) => readonly CriteriaRow[]) => void;
}

let sequence = 0;

/**
 * 🔒 The New View window's condition rows, configured once (decisions 2172, 8800): the field menu with
 * the list's columns and, on the Items list, its attribute types; the comparators each field's type
 * answers in words; the value typed as the field reads; the refusal of a blank row. The New View
 * window and the Items list's Filter window both mount this, so the two never drift apart.
 */
export function ViewCriteriaRows({ rows, columns, attributes: offeredAttributes, checked, onRows }: ViewCriteriaRowsProps) {
  // An absent list is the catalog not yet answering; an empty one is a catalog with no types (T3288).
  const attributes = offeredAttributes ?? [];
  const byKey = new Map(columns.map((column) => [column.key, column]));
  const typeOf = (key: string) => byKey.get(key)?.type ?? 'text';
  const attributeById = new Map(attributes.map((attribute) => [attribute.id, attribute]));
  const attributeOf = (field: string) => {
    const id = attributeTypeIdOf(field);
    return id === null ? null : attributeById.get(id) ?? null;
  };
  const problems = criteriaProblems(rows);
  const problemOf = (row: CriteriaRow): CriteriaRowProblem | null => {
    if (!checked) return null;
    const problem = problems[row.id];
    if (problem === 'field') return { at: 'field', text: t('view.problem.field') };
    if (problem === 'value') return { at: 'value', text: t('view.problem.value') };
    return null;
  };
  const held = new Set(rows.map((row) => row.field));
  const offered = attributes.filter((attribute) => attribute.isActive || held.has(attributeCriterionField(attribute.id)));
  // A row naming a type the catalog no longer answers keeps its field, so the row still reads as chosen.
  // Until the read answers, no row reads gone: the catalog has not said so yet (T3288).
  const gone = offeredAttributes === undefined ? [] : rows.filter((row) => attributeTypeIdOf(row.field) !== null && !attributeById.has(attributeTypeIdOf(row.field)!)).map((row) => row.field);
  const fields = [
    ...columns.map((column) => ({ value: column.key, label: column.label })),
    ...offered.map((attribute) => ({ value: attributeCriterionField(attribute.id), label: attribute.name, group: t('view.attributes') })),
    ...[...new Set(gone)].map((field) => ({ value: field, label: t('view.attributeGone'), group: t('view.attributes') })),
  ];
  const comparators = (field: string) => {
    if (field === '') return [];
    const attribute = attributeOf(field);
    if (attribute) return comparatorsForAttribute(attribute.kind).map((comparator) => ({ value: comparator, label: t(comparatorLabelKey(comparator, attributeLabelType(attribute.kind))) }));
    if (attributeTypeIdOf(field) !== null) return [];
    return comparatorsForType(typeOf(field)).map((comparator) => ({ value: comparator, label: t(comparatorLabelKey(comparator, typeOf(field))) }));
  };
  const setValue = (id: string, value: string) => onRows((all) => criteriaReducer(all, { type: 'set', id, patch: { value } }));
  return (
    <CriteriaRows
      rows={rows}
      fields={fields}
      fieldPlaceholder={t('view.pickField')}
      comparators={comparators}
      problem={problemOf}
      renderValue={(row, error) => {
        if (row.field === '' || !comparatorTakesValue(row.comparator)) return null;
        const attribute = attributeOf(row.field);
        if (attribute) return <AttributeValue attribute={attribute} value={row.value} error={error} onChange={(v) => setValue(row.id, v)} />;
        if (attributeTypeIdOf(row.field) !== null) return null;
        return <CriterionValue column={byKey.get(row.field)} value={row.value} error={error} onChange={(v) => setValue(row.id, v)} />;
      }}
      onChange={(id, patch) =>
        onRows((all) =>
          all.map((row) => {
            if (row.id !== id) return row;
            // 🔒 A new field clears the value and moves the comparator to the new type's first where the old one is not offered (decision 2172).
            if (patch.field !== undefined) {
              const attribute = attributeOf(patch.field);
              return attribute ? refieldAttributeRow(row, patch.field, attribute.kind, attribute.defaultUnitId ?? attribute.units[0]?.id ?? null) : refieldRow(row, patch.field, typeOf(patch.field));
            }
            const comparator = (patch.comparator ?? row.comparator) as ViewComparator;
            const attribute = attributeOf(row.field);
            // A Measured row keeps its unit when the comparator moves, so a value typed again needs no unit chosen again.
            const cleared = attribute?.kind === 'Measured' ? measuredCriterionValue('', measuredCriterionParts(row.value).unitId) : '';
            return { ...row, comparator, value: comparatorTakesValue(comparator) ? row.value : cleared };
          }),
        )
      }
      onRemove={(id) => onRows((all) => criteriaReducer(all, { type: 'remove', id }))}
      onAdd={() => {
        sequence += 1;
        // 🔒 A new row starts with no field chosen, so nothing is filtered until a person picks one (decision 2172).
        const row: CriteriaRow = { id: `criteria-${sequence}`, field: '', comparator: 'is', value: '' };
        onRows((all) => criteriaReducer(all, { type: 'add', row }));
      }}
    />
  );
}

/**
 * 🔒 An attribute condition's value (decision 8800): a Measured type's number beside a select of its
 * family's units, the stored value the number and the unit's id; an Enumerated type's active values,
 * with the one a row already holds kept though it was deactivated.
 */
function AttributeValue({ attribute, value, error, onChange }: { attribute: CriteriaAttribute; value: string; error: string | null; onChange: (value: string) => void }) {
  if (attribute.kind === 'Enumerated') {
    const options = attribute.values.filter((option) => option.isActive || option.id === value).map((option) => ({ value: option.id, label: option.label }));
    return <Select size={FieldSize.Column} label={t('view.value')} labelHidden value={value} onChange={onChange} options={options} placeholder={t('view.value')} error={error} dataAttribute={{ 'data-criteria-attribute-value': attribute.id }} />;
  }
  const parts = measuredCriterionParts(value);
  // A type naming no default unit stores the first unit of its family, the one the Select shows (T3288).
  const unitId = parts.unitId || attribute.defaultUnitId || attribute.units[0]?.id || '';
  const held = parts.entered !== '' && Number.isFinite(Number(parts.entered)) ? Number(parts.entered) : null;
  return (
    <div className={styles.measured} data-criteria-measured={attribute.id}>
      <NumericInput size={FieldSize.Column} label={t('view.value')} labelHidden kind="number" value={held} onChange={(next) => onChange(measuredCriterionValue(next === null ? '' : String(next), unitId))} error={error} />
      <Select size={FieldSize.Column} label={t('view.unit')} labelHidden value={unitId} onChange={(next) => onChange(measuredCriterionValue(parts.entered, next))} options={attribute.units.map((unit) => ({ value: unit.id, label: unit.label }))} dataAttribute={{ 'data-criteria-unit': attribute.id }} />
    </div>
  );
}

/**
 * The criterion's value, typed the way the list draws the column (decisions
 * 0297, 2172): a status as a select of its set, a currency in the Numeric
 * Input's currency mode over the cents the rows hold, a count as a whole
 * number, a date through the Date Picker, anything else as text. The stored
 * criterion stays a string, which is what the matcher reads. Each control fills
 * its column; the character widths of decision 0297 do not apply inside the row.
 */
function CriterionValue({ column, value, error, onChange }: { column: CriteriaColumn | undefined; value: string; error: string | null; onChange: (value: string) => void }) {
  const type = column?.type ?? 'text';
  if (type === 'status') {
    const members = statusMembersOf(column?.statusEntity);
    if (members.length > 0) {
      return <Select size={FieldSize.Column} label={t('view.value')} labelHidden value={value} onChange={onChange} options={members} placeholder={t('view.value')} error={error} />;
    }
  }
  if (type === 'currency' || type === 'number') {
    const held = value.trim() !== '' && Number.isFinite(Number(value)) ? Number(value) : null;
    // 🔒 A count reads as a whole number (decision 2172): a column that names what it counts takes no decimals.
    const whole = type === 'number' && column?.unit !== undefined ? { decimals: 0 } : {};
    return (
      <NumericInput size={FieldSize.Column} label={t('view.value')} labelHidden kind={type} value={held} onChange={(next) => onChange(next === null ? '' : String(next))} error={error} {...whole} />
    );
  }
  if (type === 'date' || type === 'instant') return <DatePicker size={Size.Medium} label={t('view.value')} labelHidden value={value} onChange={onChange} mode="date" error={error} />;
  /*
   * 🔨 A `link` column filters on the text the cell draws, never on the address
   * behind it (decision 0565), and a `pills` cell reads as one comma-joined
   * string filtered by contains (decision 1486): both take the text box.
   */
  return <TextInput size={FieldSize.Column} label={t('view.value')} labelHidden value={value} onChange={onChange} error={error} />;
}
